import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    alert("Thank you for your message! We'll be in touch soon.");
    setFormData({
      name: '',
      email: '',
      company: '',
      message: ''
    });
  };
  return <main className="pt-24" data-id="element-279">
      {/* Hero */}
      <Section background="default" data-id="element-280">
        <div className="max-w-4xl" data-id="element-281">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-[#0C0C0C] mb-6 leading-tight" data-id="element-282">
            Let's Talk About Your Project
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 leading-relaxed" data-id="element-283">
            Tell us about your goals, challenges, or ideas — we'll help you find
            the right digital solution.
          </motion.p>
        </div>
      </Section>

      {/* Contact Form & Info */}
      <Section background="white" data-id="element-284">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-285">
          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-286">
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-287">
              <div data-id="element-288">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-289">
                  Your Name *
                </label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors" placeholder="John Doe" data-id="element-290" />
              </div>

              <div data-id="element-291">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-292">
                  Email Address *
                </label>
                <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors" placeholder="john@company.com" data-id="element-293" />
              </div>

              <div data-id="element-294">
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-295">
                  Company / Organization
                </label>
                <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors" placeholder="Your Company" data-id="element-296" />
              </div>

              <div data-id="element-297">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-298">
                  Tell us about your project *
                </label>
                <textarea id="message" name="message" required rows={6} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors resize-none" placeholder="Describe your project, goals, timeline, and any other relevant details..." data-id="element-299" />
              </div>

              <Button type="submit" size="lg" className="w-full" isLoading={isSubmitting} rightIcon={<Send className="w-4 h-4" data-id="element-301" />} data-id="element-300">
                Send Message
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:pl-8" data-id="element-302">
            <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-8" data-id="element-303">
              Get in Touch
            </h2>

            <div className="space-y-8 mb-12" data-id="element-304">
              <div className="flex items-start" data-id="element-305">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-306">
                  <Mail className="w-5 h-5 text-[#00316B]" data-id="element-307" />
                </div>
                <div data-id="element-308">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-309">Email</h3>
                  <a href="mailto:hello@studio.com" className="text-gray-600 hover:text-[#00316B] transition-colors" data-id="element-310">
                    hello@studio.com
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-311">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-312">
                  <Phone className="w-5 h-5 text-[#00316B]" data-id="element-313" />
                </div>
                <div data-id="element-314">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-315">Phone</h3>
                  <a href="tel:+1234567890" className="text-gray-600 hover:text-[#00316B] transition-colors" data-id="element-316">
                    +1 (234) 567-890
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-317">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-318">
                  <MapPin className="w-5 h-5 text-[#00316B]" data-id="element-319" />
                </div>
                <div data-id="element-320">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-321">
                    Location
                  </h3>
                  <p className="text-gray-600" data-id="element-322">
                    123 Design Street
                    <br data-id="element-323" />
                    San Francisco, CA 94102
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[#F8F8F6] rounded-xl" data-id="element-324">
              <h3 className="font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-325">
                Response Time
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-326">
                We typically respond within 24-48 hours during business days.
                For urgent inquiries, please mention it in your message.
              </p>
            </div>
          </motion.div>
        </div>
      </Section>
    </main>;
}